import React, { Suspense, lazy, useEffect, useState } from "react";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Slider } from "../components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import api, { formatApiError } from "../lib/api";

const Dragon3D = lazy(() => import("../components/Dragon3D"));
import ErrorBoundary from "../components/ErrorBoundary";

const DEFAULT = {
  section: "home_hero", enabled: true, glb_url: "",
  position: [0, -0.5, 0], scale: 1.0, rotation: [0, 0, 0],
  color: "#D4AF37", emissive: 0.35, effect: "float",
  show_desktop: true, show_mobile: false, use_global_color: true,
  radius_x: 2.3, radius_y: 0.45, radius_z: 1.5, speed: 0.5,
};

const SECTIONS = [
  { id: "home_hero", label: "Hero da Home" },
  { id: "product_cards", label: "Cards de produtos" },
];

export default function AdminDragons() {
  const [configs, setConfigs] = useState([]);
  const [selected, setSelected] = useState("home_hero");
  const [form, setForm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [glbModels, setGlbModels] = useState([]);
  const [newModelName, setNewModelName] = useState("");
  const [globalCfg, setGlobalCfg] = useState({ dragon_color_default: "#D4AF37", dragon_emissive_default: 0.35 });

  const load = () => {
    api.get("/admin/dragons").then((r) => setConfigs(r.data)).catch(() => {});
    api.get("/admin/glb-models").then((r) => setGlbModels(r.data)).catch(() => {});
    api.get("/admin/settings").then((r) => {
      setGlobalCfg({
        dragon_color_default: r.data.dragon_color_default || "#D4AF37",
        dragon_emissive_default: r.data.dragon_emissive_default ?? 0.35,
      });
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const cfg = configs.find((c) => c.section === selected);
    setForm(cfg ? { ...DEFAULT, ...cfg } : { ...DEFAULT, section: selected });
  }, [configs, selected]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setPreviewKey((n) => n + 1); };

  const uploadGlb = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd, { timeout: 180000 });
      set("glb_url", r.data.url);
      const saved = await api.post("/admin/glb-models", { name: newModelName || file.filename, url: r.data.url });
      setGlbModels((prev) => [...prev, saved.data]);
      setNewModelName("");
      toast.success("Modelo GLB enviado e salvo na biblioteca");
    } catch (err) {
      toast.error(formatApiError(err, "Falha no upload do GLB"));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id;
      await api.put(`/admin/dragons/${form.section}`, payload);
      await api.put("/admin/settings", {
        dragon_color_default: globalCfg.dragon_color_default,
        dragon_emissive_default: globalCfg.dragon_emissive_default,
      });
      toast.success("Configuração do dragão salva");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;

  const knownSections = SECTIONS.map((s) => s.id);
  const sectionLabel = (id) => SECTIONS.find((s) => s.id === id)?.label || id;

  return (
    <div data-testid="admin-dragons">
      <h1 className="font-display text-3xl tracking-tight mb-3">Dragões 3D</h1>
      <p className="text-xs text-zinc-500 mb-10">Controle o modelo GLB, posição, tamanho, cor e efeito por seção. No mobile, mantenha desativado para performance.</p>

      <div className="flex items-center gap-3 mb-8">
        <Label>Seção</Label>
        <Select value={selected} onValueChange={setSelected}>
          <SelectTrigger className="w-64 bg-[#141414] border-white/10" data-testid="dragon-section-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#141414] border-white/10">
            {knownSections.map((s) => <SelectItem key={s} value={s}>{sectionLabel(s)}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2.5 text-sm text-zinc-300 ml-4">
          <Switch checked={form.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="dragon-enabled-switch" /> Ativo nesta seção
        </label>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="space-y-6">
          <div>
            <Label>Biblioteca de modelos GLB</Label>
            <div className="flex gap-2 mt-1.5 mb-3">
              <Input value={newModelName} onChange={(e) => setNewModelName(e.target.value)} placeholder="Nome do modelo (ex: Dragão jade)" data-testid="glb-model-name"
                className="bg-[#141414] border-white/10 focus:border-amber-500 text-sm" />
              <label className="shrink-0 cursor-pointer inline-flex items-center gap-1.5 px-3 border border-gold/40 text-gold rounded-md text-xs hover:bg-amber-500/10 transition-colors" data-testid="dragon-glb-upload">
                <Upload size={13} /> {uploading ? "Enviando..." : "Upload GLB"}
                <input type="file" accept=".glb" className="hidden" onChange={uploadGlb} />
              </label>
            </div>
            <div className="border border-white/10 rounded-md divide-y divide-white/5 max-h-40 overflow-y-auto" data-testid="glb-library">
              {glbModels.map((m) => (
                <div key={m.id} className={`flex items-center justify-between px-3 py-2 text-xs ${form.glb_url === m.url ? "bg-amber-500/10" : "hover:bg-white/5"}`} data-testid={`glb-model-${m.id}`}>
                  <button type="button" onClick={() => set("glb_url", m.url)} className={`flex-1 text-left transition-colors ${form.glb_url === m.url ? "text-gold" : "text-zinc-300"}`}>
                    {m.name} {form.glb_url === m.url && <span className="text-gold">(em uso)</span>}
                  </button>
                  <button type="button" data-testid={`glb-delete-${m.id}`} className="text-zinc-600 hover:text-red-400 transition-colors ml-2"
                    onClick={async () => { await api.delete(`/admin/glb-models/${m.id}`); setGlbModels((prev) => prev.filter((x) => x.id !== m.id)); }}>
                    ✕
                  </button>
                </div>
              ))}
              {glbModels.length === 0 && <p className="px-3 py-3 text-[11px] text-zinc-600">Nenhum modelo salvo ainda — faça upload do seu GLB para ativar o dragão 3D.</p>}
            </div>
            <Input value={form.glb_url} onChange={(e) => set("glb_url", e.target.value)} data-testid="dragon-glb-url"
              className="mt-2 bg-[#141414] border-white/10 focus:border-amber-500 font-mono text-xs" />
          </div>

          <div className="border border-white/10 rounded-md p-4 bg-black/30">
            <p className="label-caps mb-3">Cor padrão global (todas as seções)</p>
            <div className="grid grid-cols-2 gap-4 items-end">
              <div className="flex gap-2 items-center">
                <input type="color" value={globalCfg.dragon_color_default} data-testid="global-dragon-color"
                  onChange={(e) => setGlobalCfg({ ...globalCfg, dragon_color_default: e.target.value })}
                  className="w-10 h-10 rounded border border-white/10 bg-transparent cursor-pointer" />
                <span className="text-xs font-mono text-zinc-400">{globalCfg.dragon_color_default}</span>
              </div>
              <div>
                <Label className="text-xs">Brilho global: {globalCfg.dragon_emissive_default}</Label>
                <Slider value={[globalCfg.dragon_emissive_default]} min={0} max={1.5} step={0.05} className="mt-3" data-testid="global-dragon-emissive"
                  onValueChange={([v]) => setGlobalCfg({ ...globalCfg, dragon_emissive_default: v })} />
              </div>
            </div>
            <label className="flex items-center gap-2.5 text-xs text-zinc-400 mt-3">
              <Switch checked={!!form.use_global_color} onCheckedChange={(v) => set("use_global_color", v)} data-testid="use-global-color-switch" />
              Esta seção usa a cor global
            </label>
          </div>

          <div className="grid grid-cols-3 gap-4">
            {["X", "Y", "Z"].map((axis, i) => (
              <div key={axis}>
                <Label className="text-xs">Posição {axis}</Label>
                <Input type="number" step="0.1" value={form.position[i]} data-testid={`dragon-pos-${axis.toLowerCase()}`}
                  onChange={(e) => set("position", form.position.map((v, j) => (j === i ? parseFloat(e.target.value) || 0 : v)))}
                  className="mt-1 bg-[#141414] border-white/10" />
              </div>
            ))}
          </div>

          <div>
            <Label className="text-xs">Tamanho (escala): {form.scale}</Label>
            <Slider value={[form.scale]} min={0.2} max={4} step={0.1} onValueChange={([v]) => set("scale", v)} className="mt-3" data-testid="dragon-scale-slider" />
          </div>

          <div>
            <Label className="text-xs">Rotação Y (graus): {Math.round((form.rotation[1] * 180) / Math.PI)}°</Label>
            <Slider value={[form.rotation[1]]} min={-3.14} max={3.14} step={0.05}
              onValueChange={([v]) => set("rotation", [form.rotation[0], v, form.rotation[2]])} className="mt-3" data-testid="dragon-rotation-slider" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Cor da seção {form.use_global_color ? "(herdada da global)" : ""}</Label>
              <div className="flex gap-2 mt-1.5 items-center">
                <input type="color" value={form.use_global_color ? globalCfg.dragon_color_default : form.color} disabled={!!form.use_global_color}
                  onChange={(e) => set("color", e.target.value)} data-testid="dragon-color-input"
                  className="w-10 h-10 rounded border border-white/10 bg-transparent cursor-pointer disabled:opacity-40" />
                <span className="text-xs font-mono text-zinc-400">{form.use_global_color ? globalCfg.dragon_color_default : form.color}</span>
              </div>
            </div>
            <div>
              <Label className="text-xs">Emissão: {form.use_global_color ? globalCfg.dragon_emissive_default : form.emissive}</Label>
              <Slider value={[form.use_global_color ? globalCfg.dragon_emissive_default : form.emissive]} min={0} max={1.5} step={0.05}
                disabled={!!form.use_global_color} onValueChange={([v]) => set("emissive", v)} className="mt-4" data-testid="dragon-emissive-slider" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Tipo de efeito</Label>
            <Select value={form.effect} onValueChange={(v) => set("effect", v)}>
              <SelectTrigger className="mt-1.5 bg-[#141414] border-white/10" data-testid="dragon-effect-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-white/10">
                <SelectItem value="float">Flutuar (suave)</SelectItem>
                <SelectItem value="orbit">Órbita (giro contínuo)</SelectItem>
                <SelectItem value="sway">Balanço</SelectItem>
                <SelectItem value="static">Estático</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {selected === "product_cards" && (
            <div className="border border-white/10 rounded-md p-4 bg-black/30 space-y-5" data-testid="orbit-params">
              <p className="label-caps">Órbita ao redor do card</p>
              <div>
                <Label className="text-xs">Raio X (largura): {form.radius_x ?? 2.3}</Label>
                <Slider value={[form.radius_x ?? 2.3]} min={0.5} max={4} step={0.1} onValueChange={([v]) => set("radius_x", v)} className="mt-3" data-testid="orbit-radius-x" />
              </div>
              <div>
                <Label className="text-xs">Raio Y (altura): {form.radius_y ?? 0.45}</Label>
                <Slider value={[form.radius_y ?? 0.45]} min={0} max={2} step={0.05} onValueChange={([v]) => set("radius_y", v)} className="mt-3" data-testid="orbit-radius-y" />
              </div>
              <div>
                <Label className="text-xs">Raio Z (profundidade): {form.radius_z ?? 1.5}</Label>
                <Slider value={[form.radius_z ?? 1.5]} min={0.5} max={3} step={0.1} onValueChange={([v]) => set("radius_z", v)} className="mt-3" data-testid="orbit-radius-z" />
              </div>
              <div>
                <Label className="text-xs">Velocidade: {form.speed ?? 0.5}</Label>
                <Slider value={[form.speed ?? 0.5]} min={0.1} max={2} step={0.05} onValueChange={([v]) => set("speed", v)} className="mt-3" data-testid="orbit-speed" />
              </div>
              <p className="text-[11px] text-zinc-600">O dragão passa por trás, pela lateral e pela frente do primeiro card da grade, em loop contínuo.</p>
            </div>
          )}

          <div className="flex gap-8">
            <label className="flex items-center gap-2.5 text-sm text-zinc-300">
              <Switch checked={form.show_desktop} onCheckedChange={(v) => set("show_desktop", v)} data-testid="dragon-desktop-switch" /> Desktop
            </label>
            <label className="flex items-center gap-2.5 text-sm text-zinc-300">
              <Switch checked={form.show_mobile} onCheckedChange={(v) => set("show_mobile", v)} data-testid="dragon-mobile-switch" /> Mobile
            </label>
          </div>

          <Button onClick={save} disabled={saving} className="bg-gold text-black hover:bg-gold-light font-semibold px-10" data-testid="save-dragon-button">
            {saving ? "Salvando..." : "Salvar configuração"}
          </Button>
        </div>

        <div>
          <p className="label-caps mb-3">Pré-visualização</p>
          <div className="border border-gold/20 rounded-lg bg-black aspect-square overflow-hidden gold-glow" data-testid="dragon-preview">
            {form.enabled && form.glb_url ? (
              <ErrorBoundary fallback={<div className="w-full h-full flex items-center justify-center text-zinc-600 text-sm text-center px-6">Falha ao carregar o modelo — verifique o arquivo GLB</div>}>
                <Suspense fallback={<div className="w-full h-full flex items-center justify-center text-zinc-600 text-sm">Carregando modelo...</div>}>
                  <Dragon3D key={previewKey + form.glb_url} cfg={form} className="w-full h-full" />
                </Suspense>
              </ErrorBoundary>
            ) : form.enabled ? (
              <div className="w-full h-full flex items-center justify-center text-zinc-600 text-sm text-center px-6">Nenhum modelo GLB selecionado — faça upload do seu arquivo acima</div>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-zinc-600 text-sm">Dragão desativado nesta seção</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
